import Icon from 'common/Icon'
import { useLink } from 'react-aria'
import './Link.scss'
import { useRef } from 'react'
import NextLink from 'next/link'

type LinkVariations = 'primary' | 'secondary'

export interface LinkProps extends React.ComponentPropsWithoutRef<'link'> {
  variant?: LinkVariations
  href: string
  showBackIcon?: boolean
  iconFill?: string
  className?: string
  target?: string
  rel?: string
  containerClass?: string
  isNextLink?: boolean
  onClick?: () => void
}

/**
 * **An accessible, link Component**.
 * @param variant the type of link to show
 * @param children the text to display in the link
 * @param href the address that this link needs to navigate to
 * @param isNextLink boolean to render React nextLink wrapped around the a tag
 * @param onClick an onClick callback function
 * @author Arwa Kuterwadliwala
 */

export const Link: React.FC<LinkProps> = ({
  variant = 'primary',
  children,
  className = '',
  containerClass = '',
  href,
  showBackIcon = false,
  iconFill,
  target,
  rel,
  isNextLink = false,
  onClick,
  ...props
}) => {
  const ref = useRef()

  const { linkProps } = useLink(props, ref)
  // Ensure target="_blank" always includes noreferrer (SAST-100 / SAST-101).
  const resolvedRel =
    target === '_blank' && !rel?.includes('noreferrer')
      ? [rel, 'noreferrer'].filter(Boolean).join(' ')
      : rel

  return (
    <div className={`CLLink ${containerClass}`} role="navigation link">
      {showBackIcon && (
        <Icon
          icon="datePrevArrow"
          fill={iconFill}
          className={`CLLink__BackIcon`}
        />
      )}
      {/* TODO: When the Next.Js version is updated in future, replace <a> tag with <NextLink> */}
      {isNextLink ? (
        <NextLink
          href={href}
          passHref
          {...linkProps}
          className={`CLLink--${variant} ${className}`}
          target={target}
          rel={resolvedRel}
          onClick={onClick}
        >
          {children}
        </NextLink>
      ) : (
        <a
          {...linkProps}
          ref={ref}
          onClick={onClick}
          className={`CLLink--${variant} ${className}`}
          href={href}
          target={target}
          rel={resolvedRel}
        >
          {children}
        </a>
      )}
    </div>
  )
}
